import { useMemo } from 'react';
import { useSearchContext } from '../SearchContext';
import { escapeRegexp } from '../../../../../../lib/escapeRegex';
import { createMarkTag } from './markStyles';
import { PossibleSectionInDependencies } from '../types';

interface TableItemHighlightedTextProps {
  text: string;
  field: string;
  section: PossibleSectionInDependencies;
  tableType?: string;
  rowIndex?: number;
}

export const TableItemHighlightedText = ({
  text,
  field,
  section,
  tableType,
  rowIndex,
}: TableItemHighlightedTextProps) => {
  const { searchTerm, searchResults, arrowNavigationMatchIdx } = useSearchContext();

  const shouldHighlight = useMemo(() => {
    if (!searchTerm || !text) return false;

    const found = searchResults.some(
      (result) =>
        result.section === section &&
        result.field === field &&
        result.tableType === tableType &&
        result.rowIndex === rowIndex &&
        // Match by source text to distinguish between different elements with same field
        result.sourceText === text,
    );

    return found;
  }, [searchTerm, text, section, field, tableType, rowIndex, searchResults]);

  const highlightedText = useMemo(() => {
    if (!shouldHighlight || !searchTerm) {
      return text;
    }

    const currentMatchResult = searchResults[arrowNavigationMatchIdx];
    const matchingResults = searchResults.filter(
      (result) =>
        result.section === section &&
        result.field === field &&
        result.tableType === tableType &&
        result.rowIndex === rowIndex &&
        result.sourceText === text,
    );

    /**
     * Determine if the CURRENT match (arrow navigation position) is in THIS specific element
     * by checking if the current match's matchIndex is one of the results for this element
     */
    const isCurrentMatchInThisElement =
      currentMatchResult &&
      matchingResults.length > 0 &&
      matchingResults.some((result) => result.matchIndex === currentMatchResult.matchIndex);

    // If this element contains the current match, we need to handle it specially
    if (isCurrentMatchInThisElement) {
      // Get the occurrence index of the current match
      const currentOccurrenceIndex = currentMatchResult.occurrenceIndex;

      // Split text by search term and highlight differently based on position
      const regex = new RegExp(`(${escapeRegexp(searchTerm)})`, 'gi');
      let localOccurrenceIndex = 0;

      return text.replace(regex, (match) => {
        const thisOccurrenceIndex = localOccurrenceIndex;
        localOccurrenceIndex++;

        // Highlight the specific occurrence that matches currentOccurrenceIndex in blue, others in yellow
        const isCurrent = thisOccurrenceIndex === currentOccurrenceIndex;
        return createMarkTag(match, isCurrent);
      });
    } else {
      // Regular yellow highlighting for non-current matches
      const regex = new RegExp(`(${escapeRegexp(searchTerm)})`, 'gi');
      return text.replace(regex, (match) => createMarkTag(match, false));
    }
  }, [
    text,
    shouldHighlight,
    searchTerm,
    searchResults,
    arrowNavigationMatchIdx,
    section,
    field,
    tableType,
    rowIndex,
  ]);

  if (!shouldHighlight) {
    return <>{text}</>;
  }

  return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
};
